import React, { useEffect, useMemo, useState } from "react";
import {
  IonPage,
  IonCol,
  IonRow,
  IonSpinner,
  IonGrid,
  IonButton,
  IonContent,
  IonSearchbar,
  IonList,
  IonItem,
  IonLabel,
  IonAvatar,
  IonCard,
  IonCardContent,
  IonIcon,
  useIonAlert,
} from "@ionic/react";
import {
  documentTextOutline,
  chatboxOutline,
  briefcaseOutline,
  peopleOutline,
  arrowBackOutline,
  folderOpenOutline,
  chatbubbleEllipsesOutline,
} from "ionicons/icons";
import { SchedulerDashboard } from "../admin/SchedulerDashboard";
import { db } from "../firebase";
import { collection, collectionGroup, doc, getDoc, getDocs, getCountFromServer, onSnapshot, query, setDoc, updateDoc, where } from "firebase/firestore";
import { HEADERTEXT_TYPES, WORKFLOW_WERKER_TYPES, WORKFLOW_SCHEDULER_TYPES, FB_COLLECTION_PATH } from "../functions/postActionTypes";
import { WerkerSchedulerHeader } from "../components/WerkHeader";
import { PayingUsers } from "../admin/PayingUsers";
import { DoubleBookings } from "../admin/DoubleBookings";
import { SchedulerRanking } from "../admin/SchedulerRanking";
import { isDesktop } from "../utils/platformUtils";
import { AdminSidebar } from "../components/layouts/AdminSidebar";
import WebHeader from "../components/layouts/WebHeader";
import { SUPPORT_MONKEY_UID } from "../constants/supportConstants";
import { SupportMonkeyChatPanel } from "../components/desktop/SupportMonkeyChatPanel";
import { DeleteUserDocs } from "../admin/DeleteUserDocs";
import { UsersReport } from "../admin/UsersReport";

// import "../theme/AppStyles.css";


const AdminView: React.FC = () => {
  const [impersonate, setImpersonate] = useState<Boolean>(false);
  const [schedDashboard, setSchedDashboard] = useState<Boolean>(false);
  const [payingUsers, setPayingUsers] = useState<Boolean>(false);
  const [doubleBookings, setDoubleBookings] = useState<Boolean>(false);
  const [_schedulerJobsDetails, setSchedulerJobsDetails] = useState<any[]>([]);
  const [schedulerJobsSummary, setSchedulerJobsSummary] = useState<any[]>([]);
  const [payingUsersList, setPayingUsersList] = useState<any[]>([]);
  const [doubleBookingsList, setDoubleBookingsList] = useState<any[]>([]);
  const [schedulerRankingList, setSchedulerRankingList] = useState<any[]>([]);
  const [schedulerRanking, setSchedulerRanking] = useState<Boolean>(false);
  const [usersReport, setUsersReport] = useState<Boolean>(false);
  const [deleteUserDocs, setDeleteUserDocs] = useState<Boolean>(false);
  const [loading, setLoading] = useState<Boolean>(false);
  const [activeMenuItem, setActiveMenuItem] = useState<string>('');
  const [present] = useIonAlert();

  // Impersonate / User Lookup state
  const [userSearchText, setUserSearchText] = useState<string>('');
  const [userSearchResults, setUserSearchResults] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [searchPerformed, setSearchPerformed] = useState<boolean>(false);

  // Profile Docs Review state
  const [profileDocsData, setProfileDocsData] = useState<Record<string, any[]>>({});
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [activeReviewSection, setActiveReviewSection] = useState<string | null>(null);
  const [profileDocsLoading, setProfileDocsLoading] = useState<boolean>(false);
  const [supportChats, setSupportChats] = useState<any[]>([]);
  const [supportChatSearchText, setSupportChatSearchText] = useState<string>("");
  const [selectedSupportChatId, setSelectedSupportChatId] = useState<string | null>(null);
  const [supportMonkeysUnreadCount, setSupportMonkeysUnreadCount] = useState<number>(0);

  // console.log("AdminView");

  const handleClearPanel = () => {
    // console.log("clear panel button clicked");
    setImpersonate(false);
    setSchedDashboard(false);
    setPayingUsers(false);
    setDoubleBookings(false);
    setSchedulerRanking(false);
    setUsersReport(false);
    setDeleteUserDocs(false);
    setActiveMenuItem('');
    // Clear impersonate state
    setUserSearchText('');
    setUserSearchResults([]);
    setSelectedUser(null);
    setSearchPerformed(false);
    // Clear profile docs review state
    setProfileDocsData({});
    setSelectedCollection(null);
    setActiveReviewSection(null);
    setProfileDocsLoading(false);
    // Clear support monkeys state
    setSupportChats([]);
    setSupportChatSearchText("");
    setSelectedSupportChatId(null);
    setSupportMonkeysUnreadCount(0);
  };

  const handleSupportMonkeys = () => {
    setImpersonate(false);
    setSchedDashboard(false);
    setPayingUsers(false);
    setDoubleBookings(false);
    setSchedulerRanking(false);
    setUsersReport(false);
    setDeleteUserDocs(false);
    setUserSearchText("");
    setUserSearchResults([]);
    setSelectedUser(null);
    setSearchPerformed(false);
    setProfileDocsData({});
    setSelectedCollection(null);
    setActiveReviewSection(null);
    setProfileDocsLoading(false);
    setSupportChatSearchText("");
    setSelectedSupportChatId(null);
    setActiveMenuItem("supportMonkeys");
  };

  const handleSupportChatSelect = async (chatId: string) => {
    setSelectedSupportChatId(chatId);
    try {
      await setDoc(
        doc(db, FB_COLLECTION_PATH.Chats, SUPPORT_MONKEY_UID),
        {
          [chatId]: {
            isRead: true,
          },
        },
        { merge: true }
      );
    } catch (error) {
      console.error("Error marking support monkey chat as read:", error);
    }
  };

  useEffect(() => {
    if (!isDesktop()) return;

    const unsubscribe = onSnapshot(
      doc(db, FB_COLLECTION_PATH.Chats, SUPPORT_MONKEY_UID),
      (msgDoc) => {
        const attributes: any = msgDoc.data() || {};
        let unread = 0;

        for (const key in attributes) {
          const row = attributes[key];
          if (!row || typeof row !== "object") continue;
          if (row?.isRead === false) unread += 1;
        }

        setSupportMonkeysUnreadCount(unread);
      },
      (error) => {
        console.error("Error in support monkey unread listener:", error);
      }
    );

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!isDesktop() || activeMenuItem !== "supportMonkeys") {
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, FB_COLLECTION_PATH.Chats, SUPPORT_MONKEY_UID),
      (msgDoc) => {
        const attributes: any = msgDoc.data() || {};
        const rows: any[] = [];

        for (const key in attributes) {
          const row = attributes[key];
          if (!row || typeof row !== "object") continue;

          rows.push({
            Key: key,
            IsRead: row?.isRead === true,
            MessageDate: Number(row?.messageDate) || 0,
            MessagePreview: String(row?.messagePreview || ""),
            MessageTitle: String(row?.messageTitle || "Support Chat"),
            SendersPic: String(row?.sendersPic || "../assets/profilePic.png"),
            DMuid: String(row?.DMuid || ""),
          });
        }

        rows.sort((a, b) => {
          const aUnread = a?.IsRead === false ? 1 : 0;
          const bUnread = b?.IsRead === false ? 1 : 0;
          if (aUnread !== bUnread) return bUnread - aUnread;
          return (Number(b?.MessageDate) || 0) - (Number(a?.MessageDate) || 0);
        });

        setSupportChats(rows);
        setSelectedSupportChatId((prev) => {
          if (!prev) return prev;
          return rows.some((chat) => chat.Key === prev) ? prev : null;
        });
      },
      (error) => {
        console.error("Error in support monkey chats listener:", error);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [activeMenuItem]);

  const filteredSupportChats = useMemo(() => {
    const queryText = supportChatSearchText.trim().toLowerCase();
    if (!queryText) return supportChats;

    return supportChats.filter((chat: any) => {
      const title = String(chat?.MessageTitle || "").toLowerCase();
      const preview = String(chat?.MessagePreview || "").toLowerCase();
      return title.includes(queryText) || preview.includes(queryText);
    });
  }, [supportChats, supportChatSearchText]);

  const selectedSupportChat = useMemo(() => {
    if (!selectedSupportChatId) return null;
    return supportChats.find((chat: any) => chat.Key === selectedSupportChatId) || null;
  }, [supportChats, selectedSupportChatId]);

  const handleMenuItemClick = (action: string) => {
    // console.log(`Menu item clicked: ${action}`);
    
    switch(action) {
      case 'supportMonkeys':
        handleSupportMonkeys();
        break;
      case 'impersonate':
        setActiveMenuItem('impersonate');
        handleImpersonate();
        break;
      case 'payingUsers':
        setActiveMenuItem('payingUsers');
        present({
          header: "This process will query all paying users. Continue?",
          buttons: [
            "Cancel", 
            { 
              text: "Ok", 
              handler: () => {
                handlePayingUsers();
              }
            }
          ],
        });
        break;
      case 'schedulerDashboard':
        setActiveMenuItem('schedulerDashboard');
        handleSchedulerJobDashboard();
        break;
      case 'doubleBookings':
        setActiveMenuItem('doubleBookings');
        present({
          header: "This process will query scheduler's open jobs that are double booked. Continue?",
          buttons: [
            "Cancel", 
            { 
              text: "Ok", 
              handler: () => {
                handleSchedulerDoublebooks();
              }
            }
          ],
        });
        break;
      case 'schedulerRanking':
        setActiveMenuItem('schedulerRanking');
        present({
          header: "This process will rank the schedulers based on completed jobs in the last 45 days. Continue?",
          buttons: [
            "Cancel", 
            { 
              text: "Ok", 
              handler: () => {
                handleSchedulerRanking();
              }
            }
          ],
        });
        break;
      case 'deleteUserDocs':
        setActiveMenuItem('deleteUserDocs');
        handleClearPanel();
        setActiveMenuItem('deleteUserDocs');
        setDeleteUserDocs(true);
        break;
      case 'usersReport':
        setActiveMenuItem('usersReport');
        handleClearPanel();
        setActiveMenuItem('usersReport');
        setUsersReport(true);
        break;
      case 'clear':
        handleClearPanel();
        break;
      case 'exit':
        window.history.back();
        break;
      default:
        break;
    }
  };


  // const handleAdminButtonClick = (display: string, buttonClicked: string) => {
  //   console.log(`${buttonClicked} button clicked`);
  //   console.log(`Display: ${display}`);

  //   const doubleCheck = () => {
  //     present({
  //       header: "Send Request?",
  //       buttons: ["Cancel", { 
  //         text: "Ok", 
  //         handler: handlePayingUsers
  //       }],
  //     });

  
  // };

  // doubleCheck();




  //   return () => {}
  // };





  const handleImpersonate = () => {
    // console.log("impersonate button clicked");
    // Clear previous state and show the search UI
    setUserSearchText('');
    setUserSearchResults([]);
    setSelectedUser(null);
    setSearchPerformed(false);
    setImpersonate(true);
  };

  const handleUserSearch = async () => {
    const searchTerm = userSearchText.trim();
    if (!searchTerm) return;

    // console.log("Searching for user:", searchTerm);
    setLoading(true);
    setSearchPerformed(true);
    setSelectedUser(null);

    try {
      const searchLower = searchTerm.toLowerCase();
      const usersQuery = query(collectionGroup(db, FB_COLLECTION_PATH.Users));
      const snapshot = await getDocs(usersQuery);

      const matchedUsers: any[] = [];
      const seenUids = new Set<string>();

      snapshot.forEach((document) => {
        const userData = document.data();
        const uid = userData.uid;
        if (!uid || seenUids.has(uid)) return;

        const firstName = (userData.firstName || '').toLowerCase();
        const lastName = (userData.lastName || '').toLowerCase();
        const fullName = `${firstName} ${lastName}`;
        const company = (userData.company || '').toLowerCase();

        if (
          firstName.includes(searchLower) ||
          lastName.includes(searchLower) ||
          fullName.includes(searchLower) ||
          company.includes(searchLower)
        ) {
          seenUids.add(uid);
          matchedUsers.push({
            uid: userData.uid,
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            profilePicURL: userData.profilePicURL || '',
            company: userData.company || '',
            occupation: userData.occupation || '',
            email: userData.email || '',
            isScheduler: userData.isScheduler || false,
          });
        }
      });

      // console.log(`Found ${matchedUsers.length} matching users`);
      setUserSearchResults(matchedUsers);
    } catch (error) {
      console.error("Error searching users:", error);
    }
    setLoading(false);
  };

  const handleSelectUser = (user: any) => {
    // console.log("Selected user:", user.firstName, user.lastName);
    setSelectedUser(user);
    setUserSearchResults([]);
    setUserSearchText('');
    setSearchPerformed(false);
  };

  const handleReviewProfileDocs = async () => {
    if (!selectedUser) return;
    const uid = selectedUser.uid;
    // console.log(`Fetching profile docs for user: ${uid}`);
    setProfileDocsLoading(true);
    setActiveReviewSection('profileDocs');
    setSelectedCollection(null);

    const subcollectionNames = [
      'announcementFeed',
      'jobFeed',
      'notifications',
      'users_BadgeCount',
      'users_NotificationsCount',
      'users_PublicFacing',
      'users_AssociateFacing',
      'jobFeedUpdatePending',
      'notificationpending',
    ];

    try {
      // Fetch root user document + userSettings doc + all subcollections in parallel
      const [rootDocSnap, userSettingsSnap, ...subcollectionSnaps] = await Promise.all([
        getDoc(doc(db, 'users', uid)),
        getDoc(doc(db, 'userSettings', uid)),
        ...subcollectionNames.map((name) =>
          getDocs(collection(db, 'users', uid, name))
        ),
      ]);

      const results: Record<string, any[]> = {};

      // Root user document
      if (rootDocSnap.exists()) {
        results['User Document'] = [{ id: rootDocSnap.id, ...rootDocSnap.data() }];
      }

      // userSettings document
      if (userSettingsSnap.exists()) {
        results['userSettings'] = [{ id: userSettingsSnap.id, ...userSettingsSnap.data() }];
      }

      // Subcollections — only include non-empty ones
      subcollectionNames.forEach((name, index) => {
        const snap = subcollectionSnaps[index];
        if (!snap.empty) {
          const docs: any[] = [];
          snap.forEach((d) => docs.push({ id: d.id, ...d.data() }));
          results[name] = docs;
        }
      });

      // console.log(`Loaded ${Object.keys(results).length} collections for user ${uid}`);
      setProfileDocsData(results);
    } catch (error) {
      console.error('Error fetching profile docs:', error);
    }
    setProfileDocsLoading(false);
  };

  const handleUserAction = (action: string) => {
    if (!selectedUser) return;
    // console.log(`Action: ${action} for user: ${selectedUser.firstName} ${selectedUser.lastName}`);
    switch (action) {
      case 'profileDocs':
        handleReviewProfileDocs();
        break;
      default:
        break;
    }
  };

// cttWKh2RAdh9mBCJGQYYdWhwfwd2 - Hudson
// Find jobs that are scheduler = closed but werker's have them as available
// Enter a scheduler's UID and find all jobs that are closed but have werkers assigned

  const handleSchedulerJobDashboard = async () => {
    // console.log("scheduler job dashboard button clicked");

    // Display a summary of all scheduled jobs be each scheduler
    // Query the users collection for all schedulers
    // For each scheduler, query their jobs and summarize the status
    // Display the summary in a table or list format

    setLoading(true);
    const today = Date.now(); // Current timestamp in milliseconds
    const schedulerQuery = query(
        collectionGroup(db, FB_COLLECTION_PATH.Users),
        where("userLastRoleScheduler", "==", true),
        where("userSubscriptionExpiration", ">", today)
      );
    const snapshot = await getDocs(schedulerQuery);

    const uniqueSchedIds: any[] = [];
    snapshot.forEach((doc) => {
      const schedulerData = doc.data();
      // console.log(`Scheduler UID: ${schedulerData.uid}, Name: ${schedulerData.firstName} ${schedulerData.lastName}`);
      // 
      const uniqueSchedId = schedulerData.uid;
      uniqueSchedIds.push(uniqueSchedId);
      
    });
    const uniqueSchedArray = Array.from(new Set(uniqueSchedIds));
    // console.log(`Found ${uniqueSchedArray.length} unique schedulers:`, uniqueSchedArray);

    const jobSummariesTemp: any[] = [];
    const jobDetailsTemp: any[] = [];

    for (const schedInfo of uniqueSchedArray) {
      // console.log("Processing scheduler UID:", schedInfo);

      var schedulerUID = "";
      var schedulerName = "";
      var schedulerPICURL = "";

      var jobsOpen = 0;
      var jobsFilled = 0;
      var jobsPast = 0;

      var iterationCount = 0;


      try {
        
      const jobQuery = query(
        collectionGroup(db, FB_COLLECTION_PATH.JobsDocs),
        where("docStamp.userInfoFrom.uid", "==", schedInfo),
        where("docStamp.userInfoTo.uid", "==", schedInfo)
      );
      const snapshot = await getDocs(jobQuery);

  
        for (const docSnap of snapshot.docs) {
          const jobData = docSnap.data();
          jobDetailsTemp.push(jobData);

          if (iterationCount === 0) {
            schedulerUID = jobData.docStamp.userInfoFrom.uid;
            schedulerName = `${jobData.docStamp.userInfoFrom.firstName} ${jobData.docStamp.userInfoFrom.lastName}`;
            schedulerPICURL = jobData.docStamp.userInfoFrom.profilePicURL;
          }
          iterationCount += 1;

          if (jobData.jobInfo.status.jobStatusSched === WORKFLOW_SCHEDULER_TYPES.Open) {
            jobsOpen += 1;
          } else if (jobData.jobInfo.status.jobStatusSched === WORKFLOW_SCHEDULER_TYPES.Filled) {
            jobsFilled += 1;
          } else if (jobData.jobInfo.status.jobStatusSched === WORKFLOW_SCHEDULER_TYPES.Past) {
            jobsPast += 1;
          }
        }
        // Create and push summary object
        const summary = {
          schedulerUID: schedulerUID,
          schedulerName: schedulerName,
          schedulerPICURL: schedulerPICURL,
          jobsOpen: jobsOpen,
          jobsFilled: jobsFilled,
          jobsPast: jobsPast,
          totalJobs: jobsOpen + jobsFilled + jobsPast
        };
        if(summary.totalJobs > 0) {
          jobSummariesTemp.push(summary);
        }
      
      } catch (error) {
        console.error("Error fetching jobs for scheduler:", error);  
      }
    }
    // console.log("Scheduler Job Summaries:", jobSummariesTemp);
    // console.log("Scheduler Job Details:", jobDetailsTemp);
    setSchedulerJobsSummary(jobSummariesTemp);
    setSchedulerJobsDetails(jobDetailsTemp);
    setSchedDashboard(true);
    setLoading(false);
  };


  const handleJobAnalysis = async () => {
  
    // console.log("job analysis button clicked");
    // Query all jobs by a specific scheduler
    const jobQuery = query(
        collectionGroup(db, FB_COLLECTION_PATH.JobsDocs),
        where("docStamp.userInfoFrom.uid", "==", "cttWKh2RAdh9mBCJGQYYdWhwfwd2"),
        where("jobInfo.status.jobStatusSched", "==", WORKFLOW_SCHEDULER_TYPES.Filled)
      );
    const snapshot = await getDocs(jobQuery);
    
    // Organize the job information by uniqueJobId
    const uniqueJobIds: string[] = [];
    snapshot.forEach((doc) => {

      const jobData = doc.data();
      const uniqueJobId = jobData.jobInfo?.core?.uniqueJobId;
      uniqueJobIds.push(uniqueJobId);
    });
    const uniqueArray = Array.from(new Set(uniqueJobIds));
    // console.log(`Found ${uniqueArray.length} unique jobs for scheduler:`, uniqueArray);

    const jobSummaries: any[] = [];
    for (const jobId of uniqueArray) {
      var jobIdentifier = "";
      var jobSlots = 0;
      var jobStatusSched = "";
      var schedulerUID_loop = "";

      for (const docSnap of snapshot.docs) {
        const jobData = docSnap.data();
        if (jobData.jobInfo?.core?.uniqueJobId === jobId) {
          if (jobData.docStamp.userInfoTo.uid === jobData.docStamp.userInfoFrom.uid) {
            // This is the scheduler's document
            jobIdentifier = jobData.jobInfo.core.uniqueJobId;
            jobSlots = jobData.jobInfo.core.numJobSlots;
            jobStatusSched = jobData.jobInfo.status.jobStatusSched;
            schedulerUID_loop =  jobData.docStamp.userInfoFrom.uid;
          }
        }
      }

    // Query jobFeed collection for users with "available" status for this job
    const usersWithAvailableStatus: string[] = [];
    try {
      const jobFeedQuery = query(
        collectionGroup(db, FB_COLLECTION_PATH.JobFeed),
        where("jobId", "==", jobId),
        where("status", "==", "available")
      );
      const feedSnapshot = await getDocs(jobFeedQuery);
      
      feedSnapshot.forEach((feedDoc) => {
        // Extract UID from document path: "users/{uid}/jobFeed/{jobId}"
        const pathParts = feedDoc.ref.path.split('/');
        if (pathParts.length >= 2) {
          const userUid = pathParts[1]; // users/{uid}/jobFeed/{jobId}
          usersWithAvailableStatus.push(userUid);
        }
      });
      
      // console.log(`Job ${jobId}: Found ${usersWithAvailableStatus.length} users with "available" status in jobFeed`);
    } catch (error) {
      console.error(`Error querying jobFeed for job ${jobId}:`, error);
    }

      

    // Create and push summary object
  const summary = {
    jobIdentifier: jobIdentifier,
    schedulerUID: schedulerUID_loop,
    jobSlots: jobSlots,
    jobStatusSched: jobStatusSched,
    usersWithAvailableStatus: usersWithAvailableStatus,
    availableCount: usersWithAvailableStatus.length
  };

    if (summary.jobStatusSched === WORKFLOW_SCHEDULER_TYPES.Filled && summary.availableCount > 0) {
      jobSummaries.push(summary);
    }

  }
  // console.log("Job Summaries:", jobSummaries);
  // for (const js of jobSummaries) {
  //   fixJobBugs(js.jobIdentifier, js.schedulerUID);
  // }
}


  const _fixJobBugs = async (uniqueJobId: string, schedulerUID: string) => {
    // console.log(`Fixing job bugs for jobId: ${uniqueJobId}, schedulerUID: ${schedulerUID}`);
    try {
      const werkersQuery = query(
        collectionGroup(db, FB_COLLECTION_PATH.JobFeed),
        where("jobId", "==", uniqueJobId),
        where("schedUid", "==", schedulerUID  ),
        where("status", "==", "available"),
      );

      const snapshot = await getDocs(werkersQuery);
      snapshot.forEach(async (document) => {
        // console.log("Buggy werker job found:", document.id, document.data());
        // const tempUserDoc = document.ref;
        // console.log("Temp user doc ref:", tempUserDoc.path);

        // Fix the job status to Expired
        await updateDoc(document.ref, {
          "status": "expired",
          "updatedAt": Date.now()
        });
        // console.log(`Updated ${document.ref.path} to expired`);
      });

    } catch (error) {
      console.error("Error fetching job bugs:", error);
      throw error;
    }
  }

  const handlePayingUsers = async () => {
    // console.log("paying users button clicked");
    setPayingUsers(true);
    // Navigate to paying schedulers view
    try {

      const payingUsersQuery = query(
        collectionGroup(db, FB_COLLECTION_PATH.Users),
        // where("isScheduler", "==", true), 
        where("userSubscriptionExpiration", ">", Date.now() )
      );

      const snapshot = await getDocs(payingUsersQuery);
      const tempUsersList: React.SetStateAction<Boolean> | { userUID: any; userName: string; userPICURL: any; userScheduler: boolean; }[] = [];
      snapshot.forEach((document) => {
        const userData = document.data();
       

          // console.log(userData);
          tempUsersList.push({
            userUID: userData.uid,
            userName: `${userData.firstName} ${userData.lastName}`,
            userPICURL: userData.profilePicURL,
            userScheduler: userData.isScheduler,
          });
        
      });
      setPayingUsersList(tempUsersList);
    } catch (error) {
      // console.log(error)    
    }
  };

  const handleSchedulerDoublebooks = async () => {
    // console.log("scheduler doublebooks button clicked");

    // List of scheduler's with open jobs
    // List of werkers assigned to those jobs
    // Indicate if there's more werkers than job slots

    setLoading(true);
    // Query all open jobs
    try {

      // GET THE PAYING USERS FIRST
      const payingUsersQuery = query(
        collectionGroup(db, FB_COLLECTION_PATH.Users),
        // where("isScheduler", "==", true), 
        where("userSubscriptionExpiration", ">", Date.now() )
      );

      const snapshot = await getDocs(payingUsersQuery);
      const tempUsersList: any[] = [];
      snapshot.forEach((document) => {
        const userData = document.data();
          tempUsersList.push({
            userUID: userData.uid,
            userName: `${userData.firstName} ${userData.lastName}`,
            userPICURL: userData.profilePicURL,
            userScheduler: userData.isScheduler,
          });
        
      });

      const doubleBookingsResults: any[] = [];

      // GET THE OPEN JOBS FOR EACH SCHEDULER
      for (const user of tempUsersList) {
        // console.log("Processing scheduler:", user.userName);
        const schedulerDoubleBookings: any[] = [];
        
        // For each scheduler, query their open jobs
        const jobsQuery = query(
          collectionGroup(db, FB_COLLECTION_PATH.JobsDocs),
          where("docStamp.userInfoFrom.uid", "==", user.userUID),
          where("docStamp.userInfoTo.uid", "==", user.userUID),
          where("jobInfo.status.jobStatusSched", "==", WORKFLOW_SCHEDULER_TYPES.Filled)
        );
        const jobsSnapshot = await getDocs(jobsQuery);
        
        // Process each job for this scheduler
        const jobPromises = jobsSnapshot.docs.map(async (jobDoc) => {
          const jobData = jobDoc.data();
          
          const werkersAssigned = query(
            collectionGroup(db, FB_COLLECTION_PATH.JobsDocs),
            where("docStamp.userInfoTo.uid", "!=", user.userUID),
            where("jobInfo.core.uniqueJobId", "==", jobData.jobInfo.core.uniqueJobId),
            where("jobInfo.status.jobStatusWerker", "in", [WORKFLOW_WERKER_TYPES.Scheduled, WORKFLOW_WERKER_TYPES.Past])
          );
          
          // Get number of werkers assigned
          const werkersSnapshot = await getDocs(werkersAssigned);
          const numWerkersAssigned = werkersSnapshot.size;
          const numJobSlots = jobData.jobInfo.core.numJobSlots;

          // Collect werker names
          const werkerNames: string[] = [];
          werkersSnapshot.forEach((werkerDoc) => {
            const werkerData = werkerDoc.data();
            const werkerName = `${werkerData.docStamp.userInfoTo.firstName} ${werkerData.docStamp.userInfoTo.lastName}`;
            werkerNames.push(werkerName);
          });
            
          schedulerDoubleBookings.push({
            jobId: jobData.jobInfo.core.uniqueJobId,
            jobTitle: jobData.jobInfo.core.jobTitle || '',
            numJobSlots,
            numWerkersAssigned,
            overbookedBy: numWerkersAssigned - numJobSlots,
            jobDate: jobData.jobInfo.core.jobDate || '',
            werkersList: werkerNames,
          });
        });
        
        await Promise.all(jobPromises);
        
        // Only add scheduler if they have double bookings
        if (schedulerDoubleBookings.length > 0) {
          doubleBookingsResults.push({
            schedulerUID: user.userUID,
            schedulerName: user.userName,
            schedulerPICURL: user.userPICURL,
            doubleBookedJobs: schedulerDoubleBookings,
            totalDoubleBookings: schedulerDoubleBookings.length,
          });
        }
      }

      setDoubleBookingsList(doubleBookingsResults);
      setDoubleBookings(true);
      // console.log("Double bookings results:", doubleBookingsResults);

    } catch (error) {
      // console.log(error)    
    }
  
    setLoading(false);
  };


  const handleSchedulerRanking = async () => {

    // Get the paying schedulers
    // Get their completed jobs count
    // Rank them by completed jobs count

    // console.log("scheduler ranking button clicked");

    setLoading(true);
    // Query all open jobs
    try {

      // GET THE PAYING USERS FIRST
      const payingUsersQuery = query(
        collectionGroup(db, FB_COLLECTION_PATH.Users),
        // where("isScheduler", "==", true), 
        where("userSubscriptionExpiration", ">", Date.now() )
      );

      const snapshot = await getDocs(payingUsersQuery);
      const tempUsersList: any[] = [];
      snapshot.forEach((document) => {
        const userData = document.data();
          tempUsersList.push({
            userUID: userData.uid,
            userName: `${userData.firstName} ${userData.lastName}`,
            userPICURL: userData.profilePicURL,
            userScheduler: userData.isScheduler,
          });
      });

      const rankingResults: any[] = [];

      // const doubleBookingsResults: any[] = [];

      // GET THE OPEN JOBS FOR EACH SCHEDULER
      for (const user of tempUsersList) {
        // console.log("Processing scheduler:", user.userName);
        // const schedulerDoubleBookings: any[] = [];
        
        // For each scheduler, query their open jobs

        const now = new Date();
        const fortyFiveDaysAgo = new Date(now.getTime() - 45 * 24 * 60 * 60 * 1000);
        const fortyFiveDaysAgoISO = fortyFiveDaysAgo.toISOString().slice(0, 16); // Format: "YYYY-MM-DDTHH:MM"

        const jobsQuery = query(
          collectionGroup(db, FB_COLLECTION_PATH.JobsDocs),
          where("docStamp.userInfoFrom.uid", "==", user.userUID),
          where("docStamp.userInfoTo.uid", "==", user.userUID),
          where("jobInfo.status.jobStatusSched", "in", [WORKFLOW_SCHEDULER_TYPES.Past]),
          where("jobInfo.status.jobCancelledByScheduler", "==", false),
          where("jobInfo.core.dateTimeFinish", ">=", fortyFiveDaysAgoISO)
        );

        // const jobsQuery = query(
        //   collectionGroup(db, FB_COLLECTION_PATH.JobsDocs),
        //   where("docStamp.userInfoFrom.uid", "==", user.userUID),
        //   where("docStamp.userInfoTo.uid", "==", user.userUID),
        //   where("jobInfo.status.jobStatusSched", "in", ["Filled", "Past"]),
        //   where("jobInfo.status.jobCancelledByScheduler", "==", false)
        // );
        // const jobsSnapshot = await getDocs(jobsQuery);
        const jobsSnapshot = await getCountFromServer(jobsQuery);
        const completedJobsCount = jobsSnapshot.data().count;

        // console.log(`Scheduler: ${user.userName}, Completed Jobs Count: ${jobsSnapshot.data().count}`);
        
        // Only add schedulers with completed jobs
      if (completedJobsCount > 0) {
        rankingResults.push({
          schedulerUID: user.userUID,
          schedulerName: user.userName,
          schedulerPICURL: user.userPICURL,
          completedJobsCount: completedJobsCount,
        });
      }

      // Sort by completed jobs count (highest to lowest)
    rankingResults.sort((a, b) => b.completedJobsCount - a.completedJobsCount);

    setSchedulerRankingList(rankingResults);
    setSchedulerRanking(true);
    // console.log("Scheduler ranking results:", rankingResults);
        

      }
    } catch (error) {
      // console.log(error)    
    }
  
    setLoading(false);


  }



  return (
    <>
      {isDesktop() ? (
        // Desktop Layout with Sidebar
        <IonPage style={{ maxWidth: 'none', width: '100vw' }}>
          <WebHeader title="Admin View" />
          
          {loading && 
            <IonSpinner 
              name="crescent" 
              color="primary" 
              style={{ 
                position: "fixed", 
                top: "50%", 
                left: "50%", 
                transform: "translate(-50%, -50%)", 
                zIndex: 1000 
              }} 
            />
          }
          
          <IonContent 
            scrollY={false} 
            style={{ 
              '--padding-start': '0px', 
              '--padding-end': '0px', 
              '--padding-top': '0px', 
              '--padding-bottom': '0px',
              '--ion-grid-width': '100%',
              maxWidth: 'none',
              width: '100%'
            } as React.CSSProperties}
          >
            <div style={{ height: '100%', width: '100%', maxWidth: 'none', display: 'flex', flexDirection: 'row' }}>
              {/* Left Sidebar Navigation */}
              <div style={{ width: '16.666%', height: '100%', overflowY: 'auto', padding: 0, flexShrink: 0 }}>
                <AdminSidebar
                  onMenuItemClick={handleMenuItemClick}
                  activeItem={activeMenuItem}
                  supportMonkeysUnreadCount={supportMonkeysUnreadCount}
                />
              </div>
              
              {/* Main Content Area */}
              <div style={{ flex: activeReviewSection || activeMenuItem === 'supportMonkeys' ? undefined : 1, width: activeReviewSection ? '30%' : activeMenuItem === 'supportMonkeys' ? '33.333%' : undefined, height: '100%', padding: activeMenuItem === 'supportMonkeys' ? '0' : '20px', overflowY: 'auto', minWidth: 0, flexShrink: activeReviewSection || activeMenuItem === 'supportMonkeys' ? 0 : undefined }}>
                {activeMenuItem === 'supportMonkeys' && (
                  <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '12px 12px 8px 12px', borderBottom: '1px solid var(--ion-color-light-shade)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <IonIcon icon={chatbubbleEllipsesOutline} color="primary" />
                        <h3 style={{ margin: 0 }}>Support Monkeys</h3>
                      </div>
                      <IonSearchbar
                        value={supportChatSearchText}
                        onIonChange={(e) => setSupportChatSearchText(e.detail.value || '')}
                        animated
                        placeholder="Search support chats"
                      />
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto' }}>
                      <IonList>
                        {filteredSupportChats.map((chat: any) => {
                          const isSelected = selectedSupportChatId === chat.Key;
                          const dateStr = chat?.MessageDate ? new Date(chat.MessageDate).toLocaleString() : '';

                          return (
                            <IonItem
                              key={chat.Key}
                              button
                              onClick={() => handleSupportChatSelect(chat.Key)}
                              color={isSelected ? 'light' : undefined}
                              style={{
                                borderLeft: isSelected ? '4px solid var(--ion-color-primary)' : '4px solid transparent',
                                fontWeight: isSelected ? 600 : 400,
                              }}
                            >
                              <IonAvatar slot="start">
                                <img
                                  src={chat.SendersPic || '../assets/profilePic.png'}
                                  alt=""
                                  onError={(e: any) => {
                                    e.target.src = '../assets/profilePic.png';
                                  }}
                                />
                              </IonAvatar>
                              <IonLabel>
                                <h2 style={{ fontWeight: chat.IsRead ? 400 : 700 }}>{chat.MessageTitle}</h2>
                                <p
                                  style={{
                                    fontWeight: chat.IsRead ? 300 : 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {chat.MessagePreview}
                                </p>
                                <p style={{ fontSize: '0.75rem', opacity: 0.7 }}>{dateStr}</p>
                              </IonLabel>
                              {!chat.IsRead && (
                                <IonIcon slot="end" icon={chatbubbleEllipsesOutline} color="danger" />
                              )}
                            </IonItem>
                          );
                        })}

                        {filteredSupportChats.length === 0 && (
                          <IonItem lines="none">
                            <IonLabel style={{ textAlign: 'center', padding: '40px 20px' }}>
                              <h4>No support chats yet</h4>
                              <p style={{ opacity: 0.7 }}>Waiting for incoming conversations</p>
                            </IonLabel>
                          </IonItem>
                        )}
                      </IonList>
                    </div>
                  </div>
                )}

                {!impersonate && !schedDashboard && !payingUsers && !doubleBookings && !schedulerRanking && !usersReport && !deleteUserDocs && activeMenuItem !== 'supportMonkeys' && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%',
                    color: '#666',
                    fontSize: '1.2em'
                  }}>
                    <p>Select an option from the menu to get started</p>
                  </div>
                )}
                {impersonate && (
                  <div>
                    {/* Profile Docs Collection List (replaces action buttons when reviewing) */}
                    {activeReviewSection === 'profileDocs' && selectedUser && (
                      <div>
                        {/* Mini user header */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                          <IonAvatar style={{ width: '40px', height: '40px' }}>
                            <img
                              src={selectedUser.profilePicURL || '/assets/icon/default-avatar.png'}
                              alt={`${selectedUser.firstName} ${selectedUser.lastName}`}
                              onError={(e) => { (e.target as HTMLImageElement).src = '/assets/icon/default-avatar.png'; }}
                              style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                            />
                          </IonAvatar>
                          <div style={{ flex: 1 }}>
                            <h3 style={{ margin: 0, fontSize: '1.1em' }}>{selectedUser.firstName} {selectedUser.lastName}</h3>
                            <p style={{ margin: 0, color: '#888', fontSize: '0.85em' }}>{selectedUser.occupation || selectedUser.company || ''}</p>
                          </div>
                        </div>

                        {/* Back button */}
                        <IonButton
                          fill="clear"
                          size="small"
                          onClick={() => {
                            setActiveReviewSection(null);
                            setSelectedCollection(null);
                            setProfileDocsData({});
                          }}
                          style={{ marginBottom: '12px' }}
                        >
                          <IonIcon icon={arrowBackOutline} slot="start" />
                          Back to Actions
                        </IonButton>

                        <h3 style={{ marginBottom: '8px', color: '#444' }}>Collections</h3>

                        {profileDocsLoading ? (
                          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                            <IonSpinner name="crescent" color="primary" />
                          </div>
                        ) : Object.keys(profileDocsData).length === 0 ? (
                          <p style={{ color: '#999' }}>No data found for this user.</p>
                        ) : (
                          <IonList>
                            {Object.entries(profileDocsData).map(([collName, docs]) => (
                              <IonItem
                                key={collName}
                                button
                                onClick={() => setSelectedCollection(collName)}
                                lines="inset"
                                style={{
                                  cursor: 'pointer',
                                  '--background': selectedCollection === collName ? 'var(--ion-color-primary-tint, #e8f0fe)' : undefined,
                                  '--color': selectedCollection === collName ? 'var(--ion-color-primary-contrast, #000)' : undefined,
                                  borderLeft: selectedCollection === collName ? '3px solid var(--ion-color-primary)' : '3px solid transparent',
                                } as React.CSSProperties}
                              >
                                <IonIcon icon={folderOpenOutline} slot="start" color={selectedCollection === collName ? 'primary' : 'medium'} />
                                <IonLabel>
                                  <h2 style={{ fontWeight: selectedCollection === collName ? 600 : 400 }}>{collName}</h2>
                                  <p style={{ color: selectedCollection === collName ? 'var(--ion-color-primary-contrast, inherit)' : undefined }}>{docs.length} doc{docs.length !== 1 ? 's' : ''}</p>
                                </IonLabel>
                              </IonItem>
                            ))}
                          </IonList>
                        )}
                      </div>
                    )}

                    {/* User Search (hidden when in review mode) */}
                    {!activeReviewSection && !selectedUser && (
                      <div style={{ maxWidth: '600px' }}>
                        <h2 style={{ marginBottom: '8px' }}>Look Up User</h2>
                        <p style={{ color: '#666', marginBottom: '16px' }}>Enter a user's name or company to search</p>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <IonSearchbar
                            value={userSearchText}
                            onIonChange={(e) => setUserSearchText(e.detail.value || '')}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleUserSearch(); }}
                            animated
                            placeholder="Search by name or company..."
                            style={{ flex: 1 }}
                          />
                          <IonButton
                            onClick={handleUserSearch}
                            disabled={!userSearchText.trim()}
                          >
                            Search
                          </IonButton>
                        </div>

                        {/* Search Results */}
                        {userSearchResults.length > 0 && (
                          <div style={{ marginTop: '16px' }}>
                            <p style={{ color: '#666', marginBottom: '8px' }}>
                              {userSearchResults.length} user{userSearchResults.length !== 1 ? 's' : ''} found — select one:
                            </p>
                            <IonList>
                              {userSearchResults.map((user) => (
                                <IonItem
                                  key={user.uid}
                                  button
                                  onClick={() => handleSelectUser(user)}
                                  lines="inset"
                                  style={{ cursor: 'pointer' }}
                                >
                                  <IonAvatar slot="start">
                                    <img
                                      src={user.profilePicURL || '/assets/icon/default-avatar.png'}
                                      alt={`${user.firstName} ${user.lastName}`}
                                      onError={(e) => { (e.target as HTMLImageElement).src = '/assets/icon/default-avatar.png'; }}
                                    />
                                  </IonAvatar>
                                  <IonLabel>
                                    <h2>{user.firstName} {user.lastName}</h2>
                                    <p>{user.occupation}{user.occupation && user.company ? ' — ' : ''}{user.company}</p>
                                  </IonLabel>
                                </IonItem>
                              ))}
                            </IonList>
                          </div>
                        )}

                        {/* No results message */}
                        {searchPerformed && userSearchResults.length === 0 && !loading && (
                          <p style={{ color: '#999', marginTop: '16px', textAlign: 'center' }}>
                            No users found matching "{userSearchText}"
                          </p>
                        )}
                      </div>
                    )}

                    {/* Selected User Profile Card + Actions (hidden when in review mode) */}
                    {!activeReviewSection && selectedUser && (
                      <div style={{ maxWidth: '700px' }}>
                        {/* User Profile Header */}
                        <IonCard>
                          <IonCardContent>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                              <IonAvatar style={{ width: '80px', height: '80px' }}>
                                <img
                                  src={selectedUser.profilePicURL || '/assets/icon/default-avatar.png'}
                                  alt={`${selectedUser.firstName} ${selectedUser.lastName}`}
                                  onError={(e) => { (e.target as HTMLImageElement).src = '/assets/icon/default-avatar.png'; }}
                                  style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                />
                              </IonAvatar>
                              <div style={{ flex: 1 }}>
                                <h1 style={{ margin: '0 0 4px 0', fontSize: '1.4em' }}>
                                  {selectedUser.firstName} {selectedUser.lastName}
                                </h1>
                                <p style={{ margin: '0 0 2px 0', color: '#666' }}>
                                  {selectedUser.occupation || 'No occupation listed'}
                                </p>
                                <p style={{ margin: '0', color: '#888' }}>
                                  {selectedUser.company || 'No company listed'}
                                </p>
                              </div>
                              <IonButton
                                fill="outline"
                                size="small"
                                color="medium"
                                onClick={() => {
                                  setSelectedUser(null);
                                  setSearchPerformed(false);
                                }}
                              >
                                Search Again
                              </IonButton>
                            </div>
                          </IonCardContent>
                        </IonCard>

                        {/* Action Buttons */}
                        <div style={{ marginTop: '20px' }}>
                          <h3 style={{ marginBottom: '12px', color: '#444' }}>Review Options</h3>
                          <IonList>
                            <IonItem
                              button
                              onClick={() => handleUserAction('profileDocs')}
                              lines="inset"
                              style={{ cursor: 'pointer', marginBottom: '4px' }}
                            >
                              <IonIcon icon={documentTextOutline} slot="start" color="primary" />
                              <IonLabel>
                                <h2>Review User's Profile Docs</h2>
                                <p>View profile documents and settings</p>
                              </IonLabel>
                            </IonItem>
                            <IonItem
                              button
                              onClick={() => handleUserAction('messages')}
                              lines="inset"
                              style={{ cursor: 'pointer', marginBottom: '4px' }}
                            >
                              <IonIcon icon={chatboxOutline} slot="start" color="primary" />
                              <IonLabel>
                                <h2>Review User's Messages</h2>
                                <p>View message history and conversations</p>
                              </IonLabel>
                            </IonItem>
                            <IonItem
                              button
                              onClick={() => handleUserAction('jobs')}
                              lines="inset"
                              style={{ cursor: 'pointer', marginBottom: '4px' }}
                            >
                              <IonIcon icon={briefcaseOutline} slot="start" color="primary" />
                              <IonLabel>
                                <h2>Review User's Jobs</h2>
                                <p>View scheduled and past jobs</p>
                              </IonLabel>
                            </IonItem>
                            <IonItem
                              button
                              onClick={() => handleUserAction('associates')}
                              lines="inset"
                              style={{ cursor: 'pointer', marginBottom: '4px' }}
                            >
                              <IonIcon icon={peopleOutline} slot="start" color="primary" />
                              <IonLabel>
                                <h2>Review User's Associates</h2>
                                <p>View associate connections and requests</p>
                              </IonLabel>
                            </IonItem>
                          </IonList>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {schedDashboard && <SchedulerDashboard jobsSummary={schedulerJobsSummary}/>}
                {payingUsers && <PayingUsers usersList={payingUsersList}/>}
                {doubleBookings && <DoubleBookings doubleBookingsList={doubleBookingsList}/>}
                {schedulerRanking && <SchedulerRanking rankingList={schedulerRankingList}/>}
                {usersReport && <UsersReport />}
                {deleteUserDocs && <DeleteUserDocs />}
              </div>

              {activeMenuItem === 'supportMonkeys' && (
                <div style={{ flex: 1, height: '100%', overflow: 'hidden', borderLeft: '1px solid var(--ion-border-color, #e0e0e0)', minWidth: 0 }}>
                  <SupportMonkeyChatPanel chatId={selectedSupportChatId} chatMeta={selectedSupportChat} />
                </div>
              )}

              {/* Right Detail Pane — only shown during review sections */}
              {activeMenuItem !== 'supportMonkeys' && activeReviewSection && (
                <div style={{ flex: 1, height: '100%', padding: '20px', overflowY: 'auto', borderLeft: '1px solid var(--ion-border-color, #e0e0e0)', minWidth: 0 }}>
                  {selectedCollection && profileDocsData[selectedCollection] ? (
                    <div>
                      <h2 style={{ marginBottom: '4px', color: 'var(--ion-text-color)' }}>{selectedCollection}</h2>
                      <p style={{ color: 'var(--ion-color-medium)', marginBottom: '16px', fontSize: '0.9em' }}>
                        {profileDocsData[selectedCollection].length} document{profileDocsData[selectedCollection].length !== 1 ? 's' : ''}
                      </p>
                      {profileDocsData[selectedCollection].map((docData, idx) => (
                        <IonCard key={docData.id || idx} style={{ marginBottom: '16px' }}>
                          <IonCardContent>
                            <h3 style={{ marginBottom: '12px', fontWeight: 600, color: 'var(--ion-text-color)', borderBottom: '2px solid var(--ion-border-color, #e0e0e0)', paddingBottom: '8px' }}>
                              Doc ID: {docData.id}
                            </h3>
                            <div style={{ fontSize: '0.85em' }}>
                              {Object.entries(docData)
                                .filter(([key]) => key !== 'id')
                                .sort(([a], [b]) => a.localeCompare(b))
                                .map(([key, value]) => (
                                  <div key={key} style={{ marginBottom: '10px', borderBottom: '1px solid var(--ion-border-color, #f0f0f0)', paddingBottom: '8px' }}>
                                    <div style={{ fontWeight: 700, color: 'var(--ion-text-color)', marginBottom: '2px' }}>{key}</div>
                                    {typeof value === 'object' && value !== null ? (
                                      <pre style={{
                                        background: 'var(--ion-color-light, #f8f9fa)',
                                        color: 'var(--ion-text-color)',
                                        padding: '8px 12px',
                                        borderRadius: '4px',
                                        fontSize: '0.85em',
                                        overflowX: 'auto',
                                        margin: '4px 0 0 0',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                      }}>
                                        {JSON.stringify(value, null, 2)}
                                      </pre>
                                    ) : (
                                      <span style={{ color: 'var(--ion-text-color)', wordBreak: 'break-all' }}>
                                        {String(value ?? '')}
                                      </span>
                                    )}
                                  </div>
                                ))}
                            </div>
                          </IonCardContent>
                        </IonCard>
                      ))}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'var(--ion-color-medium)' }}>
                      <p>Select a collection from the list to view its documents</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </IonContent>
        </IonPage>
      ) : (
        // Mobile Layout (Original)
        <IonPage>
          <WerkerSchedulerHeader title={HEADERTEXT_TYPES.AdminView} />
          
          {loading && 
            <IonSpinner name="crescent" color="primary" style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 1000 }} />
          }

          <IonGrid fixed={true}>
            <IonRow>
              <IonCol>
                <IonButton 
                  expand="block"
                  size="small" 
                  color="medium"
                  onClick={handleClearPanel}
                >
                  Clear Panel
                </IonButton>   
              </IonCol>
              <IonCol>
                <IonButton 
                  expand="block"
                  size="small" 
                  color="medium"
                  onClick={handleImpersonate}
                >
                  Impersonate
                </IonButton>   
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol>
                <IonButton 
                onClick={() => {
                  present({
                    header: "This process will query all paying users. Continue?",
                    buttons: [
                      "Cancel", 
                      { 
                        text: "Ok", 
                        handler: () => {
                          handlePayingUsers();
                        }
                      }
                    ],
                  });
                }} 
                expand="block" size="small" color="medium">
                  Paying Users 
                </IonButton>
                
              </IonCol>
              <IonCol>
                <IonButton expand="block" size="small" color="medium">
                  Paying Werkers 
                </IonButton>
                
              </IonCol>
            </IonRow>
            
            <IonRow>  
              <IonCol>
                <IonButton size="small" color="medium"
                  onClick={handleSchedulerJobDashboard}
                  >
                  Scheduler Job Dashboard
                </IonButton>
              </IonCol>
            </IonRow>
            
            <IonRow>  
              <IonCol>
                <IonButton size="small" color="medium"
                  onClick={() => {
                  present({
                    header: "This process will query scheduler's open jobs that are double booked. Continue?",
                    buttons: [
                      "Cancel", 
                      { 
                        text: "Ok", 
                        handler: () => {
                          handleSchedulerDoublebooks();
                        }
                      }
                    ],
                  });
                }} 
                  >
                  Scheduler Doublebooks
                </IonButton>
              </IonCol>
            </IonRow>
            
            <IonRow>  
              <IonCol>
                <IonButton size="small" color="medium"
                  onClick={() => {
                  present({
                    header: "This process will rank the schedulers based on completed jobs in the last 45 days. Continue?",
                    buttons: [
                      "Cancel", 
                      { 
                        text: "Ok", 
                        handler: () => {
                          handleSchedulerRanking();
                        }
                      }
                    ],
                  });
                }} 
                  >
                  Scheduler Ranking
                </IonButton>
              </IonCol>
            </IonRow>

            <IonRow>  
              <IonCol>
                <IonButton expand="block" size="small" color="danger"
                  onClick={handleJobAnalysis}
                  disabled={true}
                >
                  Fix Schedulers Jobs
                </IonButton>
              </IonCol>
              <IonCol>
                <IonButton expand="block" size="small" color="medium"
                  onClick={() => {window.history.back();}}
                  >
                  EXIT ADMIN
                </IonButton>
              </IonCol>
            </IonRow>
          <IonContent>
            {impersonate && (
              <div style={{ padding: '16px' }}>
                {/* User Search */}
                {!selectedUser && (
                  <div>
                    <h2 style={{ marginBottom: '8px' }}>Look Up User</h2>
                    <p style={{ color: '#666', marginBottom: '16px' }}>Enter a user's name or company to search</p>
                    <IonSearchbar
                      value={userSearchText}
                      onIonChange={(e) => setUserSearchText(e.detail.value || '')}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleUserSearch(); }}
                      animated
                      placeholder="Search by name or company..."
                    />
                    <IonButton
                      onClick={handleUserSearch}
                      disabled={!userSearchText.trim()}
                      expand="block"
                      size="small"
                    >
                      Search
                    </IonButton>

                    {userSearchResults.length > 0 && (
                      <div style={{ marginTop: '16px' }}>
                        <p style={{ color: '#666', marginBottom: '8px' }}>
                          {userSearchResults.length} user{userSearchResults.length !== 1 ? 's' : ''} found — select one:
                        </p>
                        <IonList>
                          {userSearchResults.map((user) => (
                            <IonItem
                              key={user.uid}
                              button
                              onClick={() => handleSelectUser(user)}
                              lines="inset"
                            >
                              <IonAvatar slot="start">
                                <img
                                  src={user.profilePicURL || '/assets/icon/default-avatar.png'}
                                  alt={`${user.firstName} ${user.lastName}`}
                                  onError={(e) => { (e.target as HTMLImageElement).src = '/assets/icon/default-avatar.png'; }}
                                />
                              </IonAvatar>
                              <IonLabel>
                                <h2>{user.firstName} {user.lastName}</h2>
                                <p>{user.occupation}{user.occupation && user.company ? ' — ' : ''}{user.company}</p>
                              </IonLabel>
                            </IonItem>
                          ))}
                        </IonList>
                      </div>
                    )}

                    {searchPerformed && userSearchResults.length === 0 && !loading && (
                      <p style={{ color: '#999', marginTop: '16px', textAlign: 'center' }}>
                        No users found matching "{userSearchText}"
                      </p>
                    )}
                  </div>
                )}

                {/* Selected User Profile Card + Actions */}
                {selectedUser && (
                  <div>
                    <IonCard>
                      <IonCardContent>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <IonAvatar style={{ width: '64px', height: '64px' }}>
                            <img
                              src={selectedUser.profilePicURL || '/assets/icon/default-avatar.png'}
                              alt={`${selectedUser.firstName} ${selectedUser.lastName}`}
                              onError={(e) => { (e.target as HTMLImageElement).src = '/assets/icon/default-avatar.png'; }}
                              style={{ width: '64px', height: '64px', objectFit: 'cover' }}
                            />
                          </IonAvatar>
                          <div style={{ flex: 1 }}>
                            <h2 style={{ margin: '0 0 4px 0' }}>{selectedUser.firstName} {selectedUser.lastName}</h2>
                            <p style={{ margin: '0 0 2px 0', color: '#666', fontSize: '0.9em' }}>{selectedUser.occupation || 'No occupation listed'}</p>
                            <p style={{ margin: '0', color: '#888', fontSize: '0.9em' }}>{selectedUser.company || 'No company listed'}</p>
                          </div>
                        </div>
                      </IonCardContent>
                    </IonCard>
                    <IonButton
                      fill="outline"
                      size="small"
                      color="medium"
                      onClick={() => { setSelectedUser(null); setSearchPerformed(false); }}
                      expand="block"
                      style={{ marginBottom: '16px' }}
                    >
                      Search Again
                    </IonButton>
                    <IonList>
                      <IonItem button onClick={() => handleUserAction('profileDocs')} lines="inset">
                        <IonIcon icon={documentTextOutline} slot="start" color="primary" />
                        <IonLabel><h2>Review User's Profile Docs</h2></IonLabel>
                      </IonItem>
                      <IonItem button onClick={() => handleUserAction('messages')} lines="inset">
                        <IonIcon icon={chatboxOutline} slot="start" color="primary" />
                        <IonLabel><h2>Review User's Messages</h2></IonLabel>
                      </IonItem>
                      <IonItem button onClick={() => handleUserAction('jobs')} lines="inset">
                        <IonIcon icon={briefcaseOutline} slot="start" color="primary" />
                        <IonLabel><h2>Review User's Jobs</h2></IonLabel>
                      </IonItem>
                      <IonItem button onClick={() => handleUserAction('associates')} lines="inset">
                        <IonIcon icon={peopleOutline} slot="start" color="primary" />
                        <IonLabel><h2>Review User's Associates</h2></IonLabel>
                      </IonItem>
                    </IonList>
                  </div>
                )}
              </div>
            )}
            {schedDashboard && <SchedulerDashboard jobsSummary={schedulerJobsSummary}/>}
            {payingUsers && <PayingUsers usersList={payingUsersList}/>}
            {doubleBookings && <DoubleBookings doubleBookingsList={doubleBookingsList}/>}
            {schedulerRanking && <SchedulerRanking rankingList={schedulerRankingList}/>}
            {usersReport && <UsersReport />}
            {deleteUserDocs && <DeleteUserDocs />}

            <br></br>
            <br></br>
            <br></br>
            <br></br>
            <br></br>
          </IonContent>
          <br></br>
          <br></br>
          <br></br>
          <br></br>
          <br></br>
          </IonGrid>
          <br></br>
          <br></br>
          <br></br>
          <br></br>
          <br></br>
        </IonPage>
      )}
    </>
  );
};

export default AdminView;
